/**
 * Pure helpers that convert one Ask Yogi markdown table into the two clipboard
 * payloads TableToolbar writes: TSV (`text/plain`) and HTML (`text/html`).
 *
 * The parser is deliberately narrow — it handles the subset of GFM tables that
 * Ask Yogi produces (leading + trailing pipes, one row per line, a `|---|---|`
 * separator between header and body). It intentionally does NOT try to be a
 * general markdown parser; ReactMarkdown + remark-gfm already own that path in
 * the render tree.
 */

export interface TableRows {
  readonly headers: readonly string[]
  readonly rows: readonly (readonly string[])[]
}

/**
 * Minimal shape of the hast (HTML AST) nodes react-markdown passes into the
 * `table:` renderer via `props.node`. react-markdown converts mdast → hast →
 * React elements, so components see `type: 'element'` with `tagName` and
 * `properties`, not mdast `type: 'table'` / 'tableRow' / 'tableCell'.
 */
interface HastNode {
  type: 'element' | 'text' | 'root'
  value?: string
  tagName?: string
  properties?: { href?: string }
  children?: HastNode[]
}

function serializeInline(node: HastNode): string {
  if (node.type === 'text') return node.value ?? ''
  if (node.type !== 'element') return ''
  const inner = serializeCellChildren(node.children ?? [])
  switch (node.tagName) {
    case 'strong':
    case 'b':
      return `**${inner}**`
    case 'em':
    case 'i':
      return `*${inner}*`
    case 'code':
      return `\`${inner}\``
    case 'a': {
      const href = node.properties?.href
      return href ? `[${inner}](${href})` : inner
    }
    case 'br':
      return ' '
    default:
      return inner
  }
}

function serializeCellChildren(nodes: HastNode[]): string {
  return nodes.map(serializeInline).join('').replace(/\|/g, '\\|').trim()
}

function findChildByTag(
  node: HastNode | undefined,
  tag: string,
): HastNode | undefined {
  return node?.children?.find((c) => c.type === 'element' && c.tagName === tag)
}

function filterChildrenByTag(
  node: HastNode | undefined,
  tag: string,
): HastNode[] {
  return (node?.children ?? []).filter(
    (c) => c.type === 'element' && c.tagName === tag,
  )
}

/**
 * Reconstruct a raw markdown table string from the hast Table node
 * react-markdown hands the `table:` renderer. Uniform across tagged
 * (`<table results="…">`) and untagged tables — the render layer always has
 * the parsed table available, unlike the upstream `tableMarkdown[]` array
 * which only tracks tagged blocks.
 */
export function reconstructTableMarkdown(node: HastNode): string {
  const thead = findChildByTag(node, 'thead')
  const tbody = findChildByTag(node, 'tbody')
  const headerRow = findChildByTag(thead, 'tr')
  const headerCells = filterChildrenByTag(headerRow, 'th').map((th) =>
    serializeCellChildren(th.children ?? []),
  )
  // Fallback: some markdown flavors put the header row directly under tbody
  // with no <thead>. In that case pull `<th>`s from the first `<tr>` we can
  // find and skip it in the body pass.
  const bodyRowNodes = filterChildrenByTag(tbody, 'tr')
  const bodyRows = bodyRowNodes.map((tr) =>
    filterChildrenByTag(tr, 'td').map((td) =>
      serializeCellChildren(td.children ?? []),
    ),
  )

  if (headerCells.length === 0 && bodyRows.length === 0) return ''

  const lines: string[] = []
  if (headerCells.length > 0) {
    lines.push(
      `| ${headerCells.join(' | ')} |`,
      `|${'---|'.repeat(headerCells.length)}`,
    )
  }
  for (const row of bodyRows) {
    lines.push(`| ${row.join(' | ')} |`)
  }
  return lines.join('\n')
}

// `<citation>…</citation>` markup can appear inside a cell. Mirrors the strip
// regex in MessageActions.convertMarkdownToText so table-scoped copy matches
// message-scoped copy.
const CITATION_STRIP_RE = /<citation>[^<]*<\/citation>/g

// A separator row is `|---|---|` (or `|:---:|`, etc.) — pipes, colons, dashes,
// and whitespace only, with at least three dashes total. Matches the guard in
// MessageActions.convertMarkdownToText.
function isSeparatorRow(line: string): boolean {
  return /^\|[\s:|-]+\|$/.test(line) && (line.match(/-/g) || []).length >= 3
}

function splitCells(line: string): string[] {
  // Split at unescaped `|`. GFM escapes `\|` inside a cell — restore those to
  // literal pipes after the split.
  const cells: string[] = []
  let current = ''
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '\\' && line[i + 1] === '|') {
      current += '|'
      i++
      continue
    }
    if (ch === '|') {
      cells.push(current)
      current = ''
      continue
    }
    current += ch
  }
  cells.push(current)
  // Drop the leading/trailing empty cells produced by the outer pipes.
  if (cells.length && cells[0]!.trim() === '') cells.shift()
  if (cells.length && cells[cells.length - 1]!.trim() === '') cells.pop()
  return cells.map((cell) => cell.replace(CITATION_STRIP_RE, '').trim())
}

export function parseMarkdownTable(tableMarkdown: string): TableRows {
  const lines = tableMarkdown
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.startsWith('|'))
    .filter((line) => !isSeparatorRow(line))

  if (lines.length === 0) {
    return { headers: [], rows: [] }
  }

  const [headerLine, ...bodyLines] = lines
  return {
    headers: splitCells(headerLine!),
    rows: bodyLines.map((line) => splitCells(line)),
  }
}

// Strip inline markdown formatting from a cell for plain-text (TSV) output.
// Ask Yogi occasionally emits **bold** headers, `code`, [links](url), etc.;
// destinations like Sheets and text editors should see clean text, not raw
// markdown syntax.
function stripInlineMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1') // **bold**
    .replace(/__([^_]+)__/g, '$1') // __bold__
    .replace(/\*([^*]+)\*/g, '$1') // *italic*
    .replace(/_([^_]+)_/g, '$1') // _italic_
    .replace(/`([^`]+)`/g, '$1') // `code`
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [label](url) → label
}

// Convert inline markdown formatting to sanitized HTML tags for the text/html
// payload. Escape HTML first so cell content can't inject arbitrary markup,
// THEN apply markdown → tag transformations against the escaped string.
function renderInlineMarkdownHtml(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
  return escaped
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
}

export function tableToTsv(rows: TableRows): string {
  const stripped = {
    headers: rows.headers.map(stripInlineMarkdown),
    rows: rows.rows.map((r) => r.map(stripInlineMarkdown)),
  }
  const lines = [
    stripped.headers.join('\t'),
    ...stripped.rows.map((r) => r.join('\t')),
  ]
  return lines.join('\n')
}

export function tableToHtml(rows: TableRows): string {
  const headCells = rows.headers
    .map((h) => `<th>${renderInlineMarkdownHtml(h)}</th>`)
    .join('')
  const bodyRows = rows.rows
    .map(
      (row) =>
        `<tr>${row.map((cell) => `<td>${renderInlineMarkdownHtml(cell)}</td>`).join('')}</tr>`,
    )
    .join('')
  return `<table><thead><tr>${headCells}</tr></thead><tbody>${bodyRows}</tbody></table>`
}
